import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, merge, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, skip, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from 'environments/environment';
import {
  Application,
  SidebarItem,
  SidebarConfig,
  ThemeService,
  SidebarLogo,
  UserProfileI,
} from '@wind-log_library/fe-component-library';
import { NavigationCategory, NavigationService } from '../../services/data-services/navigation/navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { VersionService } from 'app/shared/services/version-service/version.service';
import { ReleaseNotesService } from 'app/shared/services-api-central/release-notes/release-notes.service';
import { UserProfileService } from 'app/shared/user-profile/user-profile.service';
import {
  PortfolioLogoTheme,
  PortfolioLogoType,
  PortfolioSettingsService,
} from 'app/shared/services/user-preferences/portfolio-settings.service';
import { LayoutService } from 'app/shared/services/layout/layout.service';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  standalone: false,
})
export class SideNavComponent implements OnInit, AfterViewInit, OnDestroy {
  public application: Application = Application.WIND;
  public env = environment;
  public theme$ = new BehaviorSubject('');
  public versionInfo: string;

  private selectedModule: string = '';
  private selectedLevel: string = '';

  private sidebarConfigRAW = {
    bigScreenWidth: 1550,
    sidebarExpandedWidth: 250,
    sidebarCollapsedWidth: 64,
    collapseLabel: 'SIDE_NAV.COLLAPSE_SIDEBAR',
  };

  // ORDE OF MODULES
  // Overview
  // Monitor
  // Alarms
  // Analysis
  // Data Studio
  // Logbook
  // KPIs
  // Predictive
  // Data Quality
  // Reports
  // Budgets
  // Revenues
  // Tickets
  // Forecast
  // Planning

  public static readonly sidebarItemsRAW: SidebarItem[] = [
    {
      route: 'overview',
      module: 'module_overview_windlog',
      icon: 'assets/images/sidebar/border-all',
      iconType: 'image',
      title: 'SIDE_NAV.OVERVIEWBOARD',
    },
    {
      route: 'monitor',
      module: 'module_monitor_windlog',
      icon: 'far fa-desktop',
      title: 'SIDE_NAV.MONITOR',
    },
    {
      route: 'troubleshoot',
      module: 'module_troubleshoot_windlog',
      icon: 'far fa-bell',
      title: 'SIDE_NAV.TROUBLESHOOT',
    },
    {
      route: 'analyze',
      module: 'module_analyze_windlog',
      icon: 'far fa-analytics',
      title: 'SIDE_NAV.ANALYZE',
    },
    {
      route: 'data_studio',
      module: 'module_datastudio_windlog',
      icon: 'assets/images/sidebar/chart-scatter',
      iconType: 'image',
      title: 'SIDE_NAV.DATASTUDIO',
    },
    {
      route: 'logbook',
      module: 'module_logbook_windlog',
      icon: 'far fa-book',
      title: 'SIDE_NAV.LOGBOOK',
    },
    {
      route: 'kpi-follow-up',
      module: 'module_kpifollowup_windlog',
      icon: 'far fa-chart-pie-alt',
      title: 'SIDE_NAV.KPI',
    },
    {
      route: 'predictive',
      module: 'module_predictive_windlog',
      icon: 'far fa-monitor-heart-rate',
      title: 'SIDE_NAV.PREDICTIVE',
    },
    {
      route: 'reporting',
      module: 'module_reporting_windlog',
      icon: 'far fa-file-chart-line',
      title: 'SIDE_NAV.REPORT',
    },
    {
      route: 'budget',
      module: 'module_budget_windlog',
      icon: 'far fa-coins',
      title: 'SIDE_NAV.BUDGET',
    },
    {
      route: 'check_revenues',
      module: 'module_checkrevenues_windlog',
      icon: 'far fa-wallet',
      title: 'SIDE_NAV.REVENUES',
    },
    {
      route: 'revenues',
      module: 'module_checkrevenues_windlog',
      icon: 'far fa-wallet',
      title: 'Revenues v2',
    },
    {
      route: 'tickets',
      module: 'module_tickets_windlog',
      icon: 'far fa-clipboard-list-check',
      title: 'SIDE_NAV.TICKETS',
    },
    {
      route: 'forecast',
      module: 'module_forecast_windlog',
      icon: 'assets/images/sidebar/telescope',
      iconType: 'image',
      title: 'SIDE_NAV.FORECAST',
    },
    {
      route: 'planning',
      module: 'module_planning_windlog',
      icon: 'far fa-calendar-edit',
      title: 'SIDE_NAV.PLANNING',
    },
    {
      route: 'operate',
      module: 'module_operate_windlog',
      icon: 'far fa-headphones-alt',
      title: 'SIDE_NAV.OPERATE',
    },
    {
      route: 'de_ice',
      module: 'module_deice_windlog',
      icon: 'far fa-snowflake',
      title: 'SIDE_NAV.DEICE',
    },
    {
      route: 'pexapark',
      module: 'module_pexapark_windlog',
      icon: 'far fa-chart-line',
      title: 'SIDE_NAV.PEXAPARK',
    },
    {
      route: 'check-ppa',
      module: 'module_checkppa_windlog',
      icon: 'far fa-file-alt',
      title: 'SIDE_NAV.CHECK_PPA',
    },
    {
      route: 'performance-analytics',
      module: 'module_performanceanalytics_windlog',
      icon: 'far fa-chart-bar',
      title: 'SIDE_NAV.PERFORMANCE_ANALYTICS',
    },
  ];

  private sidebarItemsRaw: SidebarItem[] = SideNavComponent.sidebarItemsRAW;

  public sidebarConfig: SidebarConfig;
  public sidebarItems: SidebarItem[];
  private currentTheme: string = '';
  public userProfile = null;
  private currentUnreadNotes: number = 0;
  private translations;
  private readonly destroy$ = new Subject<void>();

  private readonly translationKeys = [
    ...SideNavComponent.sidebarItemsRAW.map(item => item.title),
    'SIDE_NAV.COLLAPSE_SIDEBAR',
    'SIDE_NAV.HELP',
    'SIDE_NAV.APP_VERSION',
  ];

  private currentSuperAdminId: string | null = null;
  private portfolioLogo: SidebarLogo;
  private portfolioLogoRequestId = 0;
  private sidebarWidthObserver?: ResizeObserver;

  constructor(
    private themeService: ThemeService,
    private navigationServiceNew: NavigationService,
    private translate: TranslateService,
    private releaseNotesService: ReleaseNotesService,
    private versionService: VersionService,
    private userProfileService: UserProfileService,
    private portfolioSettingsService: PortfolioSettingsService,
    private elementRef: ElementRef<HTMLElement>,
    private layoutService: LayoutService,
  ) {}

  private static readonly minCollapsedWidth = 65;

  public ngAfterViewInit(): void {
    this.sidebarWidthObserver = new ResizeObserver(([entry]) => {
      const width = Math.max(entry.contentRect.width, SideNavComponent.minCollapsedWidth);
      this.layoutService.setSidebarWidth(width);
    });
    this.sidebarWidthObserver.observe(this.elementRef.nativeElement);
  }

  public ngOnInit(): void {
    this.theme$ = this.themeService.getTheme();
    this.versionInfo = this.versionService.getFormattedVersionShort();

    this.subscribeToTranslations();
    this.subscribeToUserProfile();
    this.subscribeToNavigationChanges();
    this.subscribeToThemeChanges();
    this.subscribeToBrandingChanges();
    this.subscribeToUnreadNotes();
  }

  private subscribeToUserProfile(): void {
    this.userProfileService
      .getUserData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(profile => {
        const superAdminId = this.getSuperAdminId(profile);

        this.userProfile = {
          id: profile.id,
          firstName: profile.firstName,
          lastName: profile.lastName,
          username: profile.username,
          email: profile.email,
          position: profile.position?.[0] || '',
          company: profile.company?.[0] || '',
          appsAvailable: this.getAvailableApps(),
          id_portfolio: profile.id_portfolio || '',
          modulesAvailable: [],
          attributes: profile.attributes,
          image: '',
        };

        this.sidebarItemsRaw = this.filterInternalModules();
        this.updateSidebarItems();

        if (superAdminId !== this.currentSuperAdminId) {
          this.currentSuperAdminId = superAdminId;
          this.loadPortfolioLogo(superAdminId, this.getPortfolioLogoTheme(this.currentTheme));
        }
      });
  }

  private getAvailableApps(): string[] {
    const appList = ['app_windlog', 'app_config', 'app_management', 'app_solar', 'app_storage', 'app_energylog'];

    return appList.filter(app => this.userProfileService.hasRole(app));
  }

  private subscribeToTranslations(): void {
    merge(of(null), this.translate.onLangChange)
      .pipe(
        switchMap(() => this.translate.get(this.translationKeys)),
        takeUntil(this.destroy$),
      )
      .subscribe(translations => {
        this.translations = translations;
        this.updateSidebarConfig();
        this.updateSidebarItems();
      });
  }

  private subscribeToNavigationChanges(): void {
    this.navigationServiceNew
      .getNavigationObservable([
        NavigationCategory.Level,
        NavigationCategory.Module,
        NavigationCategory.TurbineLevelSelectedTurbine,
        NavigationCategory.ParkLevelSelectedPark,
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(navigation => {
        this.selectedLevel = navigation.level;
        this.selectedModule = navigation.module;
        this.updateSidebarItems();
      });
  }

  private subscribeToThemeChanges(): void {
    this.theme$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(theme => {
      this.currentTheme = theme;
      this.updateSidebarItems();
      this.loadPortfolioLogo(this.currentSuperAdminId, this.getPortfolioLogoTheme(theme));
    });
  }

  private subscribeToBrandingChanges(): void {
    this.portfolioSettingsService.brandingChanged$.pipe(takeUntil(this.destroy$)).subscribe(change => {
      if (change.superAdminId === this.currentSuperAdminId) {
        this.loadPortfolioLogo(this.currentSuperAdminId, this.getPortfolioLogoTheme(this.currentTheme));
      }
    });
  }

  private subscribeToUnreadNotes(): void {
    this.releaseNotesService
      .getUnreadNotes$()
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(unreadNotes => {
        this.currentUnreadNotes = unreadNotes;
        this.updateSidebarConfig();
      });
  }

  private updateSidebarConfig(): void {
    const parsedConfig = this.parseSidebarConfig();

    if (!parsedConfig) {
      return;
    }

    this.sidebarConfig = {
      ...parsedConfig,
      unreadNotes: this.currentUnreadNotes,
    };
  }

  private updateSidebarItems(): void {
    this.sidebarItems = this.parseSidebarItems();
  }

  private parseSidebarConfig(): SidebarConfig | undefined {
    if (!this.translations) {
      return undefined;
    }

    const helpTitle = this.translations['SIDE_NAV.HELP'];
    const versionTitle = this.translations['SIDE_NAV.APP_VERSION'].replace('{{version}}', this.versionInfo);

    return {
      ...this.sidebarConfigRAW,
      collapseLabel: this.translations[this.sidebarConfigRAW.collapseLabel],
      infoItems: [
        {
          title: helpTitle,
          route: 'https://mysoftware.dnv.com/download/public/greenpowermonitor/gpm-horizon-wind/gpm-horizon-wind.html',
          icon: 'far fa-question-circle',
          alt: helpTitle,
        },
        {
          title: versionTitle,
          icon: `${this.env.shared_server}/general/main-interface/icon-info-lightgrey.svg`,
          alt: versionTitle,
        },
      ],
      showWhatsNew: true,
      showFeedbackForm: true,
      logo: this.portfolioLogo,
    };
  }

  private filterInternalModules(): SidebarItem[] {
    const hasRevenuesV1 = this.userProfileService.hasAccessToRevenuesV1();
    const hasRevenuesV2 = this.userProfileService.hasAccessToRevenuesV2();
    const isInternalUser = this.userProfileService.isInternalUser();

    return SideNavComponent.sidebarItemsRAW
      .filter(item => {
        if (item.route === 'check_revenues') {
          return hasRevenuesV1;
        }

        if (item.route === 'revenues') {
          return hasRevenuesV2;
        }

        return true;
      })
      .map(item => {
        if (item.route === 'revenues' && !isInternalUser && hasRevenuesV2) {
          return {
            ...item,
            title: 'SIDE_NAV.REVENUES',
          };
        }

        return item;
      });
  }

  private parseSidebarItems(): SidebarItem[] {
    return this.sidebarItemsRaw.map(item => {
      const isActive = this.selectedModule.split('/')[0] === item.route;

      return {
        ...item,
        icon: item.iconType === 'image' ? `${item.icon}-${this.currentTheme === 'dark' ? 'dark' : 'light'}.svg` : item.icon,
        iconType: item.iconType || 'font',
        title: this.translations ? this.translations[item.title] : item.title,
        showModule: this.showModule(item.module),
        isLocked: this.isLocked([item.module]),
        isActive,
        href: item.route === 'operate' ? this.getHrefForOperate(item.route) : this.getHrefRoute(item.route),
      };
    });
  }

  public redirectToRoute = (routeModule: string, e: any) =>
    routeModule === 'operate'
      ? this.navigationServiceNew.operateRoute(routeModule, e)
      : this.navigationServiceNew.navigateToModule(routeModule, e);

  public getRouteModuleActiveClass(routeModule: string): boolean {
    return this.selectedModule && this.selectedModule === routeModule;
  }

  public getRouteLevelActiveClass(routeLevel: string): boolean {
    return this.selectedLevel && this.selectedLevel === routeLevel;
  }

  public isLocked(roles: string[]): boolean {
    return this.navigationServiceNew.isLocked(roles);
  }

  public getHrefRoute(routeModule: string): any {
    return this.navigationServiceNew.getHrefRoute(routeModule);
  }

  public getHrefForOperate(link: any) {
    return this.navigationServiceNew.getHrefForOperate(link);
  }

  public showModule(module: string) {
    return this.navigationServiceNew.showModule(module);
  }

  public handleWhatsNewClicked() {
    this.releaseNotesService.open();
  }

private loadPortfolioLogo(superAdminId: string | null, logoTheme: PortfolioLogoTheme): void {
    const requestId = ++this.portfolioLogoRequestId;
    if (!superAdminId) {
      this.portfolioLogo = undefined;
      this.updateSidebarConfig();
      return;
    }

    const cacheBust = Date.now();
    const logoThemes: PortfolioLogoTheme[] = logoTheme === 'dark' ? ['dark', 'light'] : ['light'];
    const logoRequests = (['wide', 'mini'] as PortfolioLogoType[]).flatMap(logoType =>
      logoThemes
        .map(requestedTheme => ({
          logoType,
          logoTheme: requestedTheme,
          logoUrl: this.portfolioSettingsService.getLogoUrl(superAdminId, logoType, requestedTheme, cacheBust),
        }))
        .filter(request => Boolean(request.logoUrl)),
    );
    const availableLogos: Partial<Record<PortfolioLogoType, Partial<Record<PortfolioLogoTheme, string>>>> = {};
    let pendingRequests = logoRequests.length;

    const finishRequest = (): void => {
      pendingRequests -= 1;
      if (pendingRequests > 0 || requestId !== this.portfolioLogoRequestId) {
        return;
      }

      const expandedSrc =
        this.getAvailableLogoUrl(availableLogos.wide, logoTheme) || this.getAvailableLogoUrl(availableLogos.mini, logoTheme);
      const collapsedSrc =
        this.getAvailableLogoUrl(availableLogos.mini, logoTheme) || this.getAvailableLogoUrl(availableLogos.wide, logoTheme);
      this.portfolioLogo = expandedSrc && collapsedSrc ? { expandedSrc, collapsedSrc } : undefined;
      this.updateSidebarConfig();
    };

    if (pendingRequests === 0) {
      finishRequest();
      return;
    }

    logoRequests.forEach(({ logoType, logoTheme: requestedTheme, logoUrl }) => {
      this.checkLogoUrl(
        logoUrl,
        () => {
          availableLogos[logoType] = {
            ...availableLogos[logoType],
            [requestedTheme]: logoUrl,
          };
        },
        finishRequest,
      );
    });
  }

  private getAvailableLogoUrl(
    logos: Partial<Record<PortfolioLogoTheme, string>> | undefined,
    logoTheme: PortfolioLogoTheme,
  ): string | undefined {
    return logos?.[logoTheme] || logos?.light;
  }

  private getPortfolioLogoTheme(theme: string): PortfolioLogoTheme {
    return theme === 'dark' ? 'dark' : 'light';
  }


  private checkLogoUrl(url: string, onLoad: () => void, onComplete: () => void): void {
    const image = new Image();

    image.onload = () => {
      onLoad();
      onComplete();
    };

    image.onerror = onComplete;
    image.src = url;
  }
  private getSuperAdminId(profile: UserProfileI): string | null {
    return profile?.attributes?.find(attribute => attribute.key === 'managed_by')?.value?.[0] || null;
  }

  public ngOnDestroy(): void {
    this.portfolioLogoRequestId += 1;
    this.sidebarWidthObserver?.disconnect();

    this.destroy$.next();
    this.destroy$.complete();
  }
}
